import { useEffect } from "react";
import { getItemTranslation } from "../app/utils/getItemTranslation";
import { searchLabels } from "../app/labelSearch";
import { useLabelSearchAssist } from "../app/useLabelSearchAssist";
import {
  gaEventPracticeSearchNoResult,
  type ProfilingFlow,
} from "../../tools/analytics/domains/onboarding.analytics";
import type {
  OfferedProfile,
  PracticeProfileOffer,
} from "./practiceProfileSelection";

/**
 * The practices that answer what was typed, for the two hosts of
 * `PracticeProfilesPicker` — the signup carousel and the settings editor. The
 * picker itself stays presentational: it receives the rows, it does not search
 * and it does not ask for them.
 *
 * The SEARCH lives here rather than in the picker so that the string layer runs
 * once. It used to run in both, which meant two places deciding what "found"
 * means while one of them reported the misses.
 *
 * It also owns the catalogue-miss report, and that is the point of putting them
 * together. The event used to fire on every keystroke past three characters that
 * no string matched, so "pediatrie" was filed as a missing practice and the
 * acquisition queue inherited a search defect. A hole in the catalogue is now
 * what BOTH layers failed to answer, once, on the query they both answered.
 */
export function usePracticeSearchAssist(args: {
  /** Null while the offer is still loading — there is nothing to search, and
   * nothing to call a hole either. */
  readonly offer: PracticeProfileOffer | null;
  readonly search: string;
  /** Null where the practitioner has declared no Specialty: there is no
   * catalogue, so there is nothing to assist and nothing to call a hole. */
  readonly specialtyUid: string | null;
  /** A miss at signup costs the practitioner the screen, a miss in settings
   * costs them nothing. Averaged together they would read as one number about a
   * catalogue hole that is not equally expensive in both places. */
  readonly flow: ProfilingFlow;
}): {
  /**
   * The rows that answer the search, alphabetically for the string layer and
   * the model's additions APPENDED below, or null when nothing is typed and the
   * picker shows its own unfiltered list.
   *
   * Ordered here, and the picker does not re-sort it: a row already under a
   * thumb must not move because a network answer landed two seconds later.
   */
  readonly matching: readonly OfferedProfile[] | null;
  /**
   * The uids among `matching` that the string layer did NOT find — the rows the
   * assisted search alone put on screen.
   *
   * Reported by the hosts when one of them is ticked, and that is the only
   * measure of whether the assisted layer earns its request: a practice the
   * string layer would have surfaced anyway is not one the model found.
   *
   * A set rather than a flag on the row, because the row is the catalogue's own
   * object and provenance is a fact about this SEARCH, not about the practice.
   */
  readonly assistedOnlyUids: ReadonlySet<string>;
  readonly isAssisting: boolean;
} {
  const offer = args.offer;
  const trimmed = args.search.trim();
  // Alphabetical BEFORE the search, in the reader's own language: the search
  // returns its matches in input order and appends the model's tail after them,
  // so sorting afterwards would shuffle that tail back into the list.
  const sorted =
    offer === null
      ? []
      : [...offer.docs].sort((a, b) =>
          getItemTranslation(a).localeCompare(getItemTranslation(b))
        );
  // The WHOLE catalogue, not the rows the axis answer left on screen: a practice
  // sitting on a side nobody answered has to stay findable, and the picker is
  // what separates the two afterwards.
  const local =
    trimmed === ""
      ? []
      : searchLabels({
          items: sorted,
          query: trimmed,
          labelOf: (profile) => getItemTranslation(profile),
        });

  const { assistedUids, isAssisting, settledQuery } = useLabelSearchAssist({
    catalogue:
      args.specialtyUid === null
        ? null
        : { kind: "practice", specialtyUid: args.specialtyUid },
    query: args.search,
    localResultCount: local.length,
  });

  const shown = new Set(local.map((profile) => profile.uid));
  const byUid = new Map(sorted.map((profile) => [profile.uid, profile]));
  const added = assistedUids.flatMap((uid) => {
    if (shown.has(uid)) return [];
    const profile = byUid.get(uid);
    return profile === undefined ? [] : [profile];
  });

  const specialtyUid = args.specialtyUid;
  // An offer that has not landed is not a catalogue without the practice: the
  // string layer found nothing because it had nothing to search.
  const missed =
    offer !== null &&
    settledQuery !== null &&
    local.length === 0 &&
    added.length === 0
      ? settledQuery
      : null;
  // Stamped once per settled query rather than per keystroke: the effect's
  // dependencies ARE the deduplication, so nothing has to remember what was
  // already reported.
  //
  // An effect, against the app's rule that a side effect belongs to a user
  // action callback, because the ACTION is not what is being reported. The
  // keystroke is over 400 ms before the answer that makes it a miss arrives, and
  // an `onChange` handler cannot know something two network states away.
  useEffect(() => {
    if (missed === null || specialtyUid === null) return;
    gaEventPracticeSearchNoResult({
      flow: args.flow,
      query: missed,
      specialty_uid: specialtyUid,
    });
  }, [missed, args.flow, specialtyUid]);

  return {
    matching: trimmed === "" ? null : [...local, ...added],
    // `added` IS the assisted-only tail: it was built by dropping everything the
    // string layer already showed (`shown`), which is the same subtraction this
    // set would otherwise repeat.
    assistedOnlyUids: new Set(added.map((profile) => profile.uid)),
    isAssisting,
  };
}
